<template>
    <vx-chart-column
        tooltipItemKey="tooltip"
        :loading="loading"
        :error="hasError"
        @error="chartError = true"
        :data="dataComputed"
        :colors="dataComputed"
        :axis="{
            x: {
                key: 'label',
                axis: true,
                label: '',
                ticks: 10,
                interval: 'day',
                tickFormat: (v) => dayjs.utc(v).format('MM/DD'),
            },
            y: {
                key: 'count',
                rawKey: 'rawCount',
                axis: true,
                label: '',
                ticks: 5,
                tickFormat: (v) => formatCount(v),
            },
        }"
    >
        <template v-slot:title>
            <slot name="title">
                <span> Daily New Contacts </span>
            </slot>
        </template>
        <template v-slot:subtitle>
            <slot name="subtitle"></slot>
        </template>
        <template v-slot:right>
            <slot name="right"> </slot>
        </template>
        <template v-slot:footer>
            <vx-button href="/contacts" outline color="muted-light" size="xs">
                Go to Contacts <font-awesome-icon widthAuto :icon="faArrowRight" />
            </vx-button>
        </template>

        <template v-slot:explainHeader>Daily New Contacts</template>
        <template v-slot:explainContent>
            <p>Daily Gross Count of Contacts added into Voxie platform.</p>
            <p>
                <strong>New Contacts</strong><br />
                Gross Increase Count of Contacts during the selected period.
            </p>
        </template>
    </vx-chart-column>
</template>
<script setup>
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxChartColumn } from '@voxie/frontend-components';
import { ref, computed, watch } from 'vue';
import { formatCount } from '~/components/filters';
import { analyticsClient } from '~/services';
import { calculateRangeBetweenTwoDates, lowestAndHighestExceptZeroes } from '~/utils/analytics';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import dayjs from '~/utils/dayjs';
import { formatNumber } from '~/utils/helpers';

const props = defineProps({
    startDate: {
        type: String,
        required: true,
    },
    endDate: {
        type: String,
        required: true,
    },
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
    type: {
        type: String,
        default: undefined,
        validator: (type) => ['affiliated', 'preferred'].includes(type),
    },
});

const chartError = ref(false);

const hasError = computed(() => {
    return Boolean(error.value) || chartError.value;
});

const { loading, response, error, fetchData } = useVersionedRequest();

const dataComputed = computed(() => {
    const responseData = response.value?.data;
    if (!responseData?.values?.length) {
        return [];
    }

    const [lowestValue, highestValue] = lowestAndHighestExceptZeroes(responseData.values);

    // only computes a new count when difference is bigger than 300 times than lowest value
    const shouldComputeCount = highestValue - lowestValue > 300 * lowestValue;

    const dataMapped = computedRange.value.map((date, index) => {
        const rawCount = responseData.values[index] || 0;

        return {
            label: new Date(date),
            count:
                shouldComputeCount && rawCount !== 0 ? Math.max(rawCount, Math.round(highestValue * 0.02)) : rawCount,
            rawCount,
            tooltip: formatNumber(rawCount),
            iconColor: '#14ABE0',
            bgColor: '#73D8FC',
        };
    });

    dataMapped.pop();

    return dataMapped;
});

const computedRange = computed(() =>
    calculateRangeBetweenTwoDates({
        startDate: props.startDate,
        endDate: props.endDate,
    })
);

watch(
    [() => props.startDate, () => props.endDate],
    () => {
        chartError.value = false;

        const params = {
            range: computedRange.value,
        };

        fetchData(
            analyticsClient.getContactsTotalCreated(
                {
                    teamId: Spark.state.currentTeam.id,
                    orgUnitId: props.orgUnitId,
                    groupId: props.groupId,
                },
                params,
                props.type
            )
        ).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true }
);
</script>
